import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import { getWarehouses } from '../../../services/warehousesService';
import produccionGuiaService from '../../../services/produccionGuiaService';
import ProduccionDashboardView from './DashboardView';

const STORAGE_KEY = 'produccion_dashboard_filters';

function defaultDates() {
  const to = new Date();
  const from = new Date(to.getFullYear(), to.getMonth(), 1);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { dateFrom: fmt(from), dateTo: fmt(to) };
}

function loadStoredFilters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultDates(), warehouseId: '', guideType: '' };
    const parsed = JSON.parse(raw);
    return {
      dateFrom: parsed.dateFrom || defaultDates().dateFrom,
      dateTo: parsed.dateTo || defaultDates().dateTo,
      warehouseId: parsed.warehouseId || '',
      guideType: parsed.guideType || '',
    };
  } catch {
    return { ...defaultDates(), warehouseId: '', guideType: '' };
  }
}

function saveStoredFilters(filters) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore
  }
}

export default function ProduccionDashboard() {
  const [filters, setFilters] = useState(loadStoredFilters);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    getWarehouses({ active_only: true })
      .then((r) => {
        if (r.success && r.data) setWarehouses(r.data);
      })
      .catch(() => {});
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      saveStoredFilters(next);
      return next;
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
      };
      if (filters.warehouseId) params.warehouse_id = filters.warehouseId;
      if (filters.guideType) params.guide_type = filters.guideType;

      const res = await produccionGuiaService.getDashboard(params);
      if (res.success) {
        setData(res.data);
        setMeta(res.meta);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el dashboard de producción',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard de Producción</h1>
          <p className="text-gray-600 mt-1 text-sm">
            Resumen operativo de guías, volúmenes y recetas elaboradas.
          </p>
          {meta?.date_from && meta?.date_to ? (
            <p className="text-xs text-gray-500 mt-1">
              Período {meta.date_from} → {meta.date_to}
            </p>
          ) : null}
        </div>
        <Link
          to="/produccion/guias"
          className="text-sm font-medium text-teal-700 hover:underline self-start"
        >
          Ir a guías
        </Link>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="block text-sm">
            <span className="text-gray-600 text-xs font-medium">Desde</span>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => updateFilter('dateFrom', e.target.value)}
              className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600 text-xs font-medium">Hasta</span>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => updateFilter('dateTo', e.target.value)}
              className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600 text-xs font-medium">Almacén</span>
            <select
              value={filters.warehouseId}
              onChange={(e) => updateFilter('warehouseId', e.target.value)}
              className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
            >
              <option value="">Todos</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code ? `${w.code} — ${w.name}` : w.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-gray-600 text-xs font-medium">Tipo de guía</span>
            <select
              value={filters.guideType}
              onChange={(e) => updateFilter('guideType', e.target.value)}
              className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
            >
              <option value="">Todos</option>
              <option value="producto_terminado">Producto terminado</option>
              <option value="producto_en_proceso">Producto en proceso</option>
            </select>
          </label>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={loadData}
            disabled={loading}
            className="h-10 px-4 rounded-md bg-teal-700 text-white text-sm font-medium hover:bg-teal-800 disabled:opacity-60"
          >
            {loading ? 'Cargando…' : 'Actualizar'}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="bg-white border border-gray-200 rounded-xl p-16 text-center text-gray-500">
          Cargando dashboard…
        </div>
      ) : data ? (
        <ProduccionDashboardView data={data} />
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl p-16 text-center text-gray-500">
          Sin datos para mostrar.
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
}
